/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, no-prototype-builtins */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { get, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { debounce, later } from '@ember/runloop';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';
import { getTableExportData, getTablePrintCss } from 'linced-app/utils/table';
import {
  cleanExcelSheetName,
  cleanFileName,
} from '@linc-technologies/linced-common/utils/file-names';
import { sort } from '@ember/object/computed';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import { observes } from '@ember-decorators/object';
import {
  byKeys,
  compact,
  sortBy,
  uniqBy,
} from '@linc-technologies/linced-common/utils/arrays';

// Constant Values
const defaultSortDir = 'asc';
const defaultSortProp = 'lastName';
const title = 'Audit Term Attendance Register';

export default class MoeAuditTermAttendanceRegisterController extends Controller {
  // Attrs
  SchoolStatus = SchoolStatus;

  title = title;
  queryParams = ['dateFrom', 'dateTo', 'termId'];

  // Services
  @service
  attendanceCodes;

  @service
  dates;

  @service
  excel;

  // State
  // dateFrom provides the stateful binding to a query param.
  @tracked dateFrom = '';

  // newDateFrom enables the user to enter a date without it immediately updating query params
  // as soon as the date appears "valid", i.e. when the first digit of the year is entered.
  @tracked
  newDateFrom = '';

  // dateTo provides the stateful binding to a query param.
  @tracked dateTo = '';

  // newDateTo enables the user to enter a date without it immediately updating query params
  // as soon as the date appears "valid", i.e. when the first digit of the year is entered.
  @tracked
  newDateTo = '';

  // sortDir provides the current direction of sorting.
  @tracked sortDir = defaultSortDir;

  // sortProp provides the current entity to be used to sort by.
  @tracked sortProp = defaultSortProp;

  // termId provides the stateful binding to a query param.
  @tracked termId = '';

  // Methods
  @computed('termId', 'model.terms.[]')
  get term() {
    return this.model.terms.find((item) => item.id === this.termId);
  }

  @computed('dateFrom', 'dateTo', 'model.dates.[]')
  get days() {
    return this.model.dates
      .filter((date) => {
        return (
          date.date >= this.dateFrom &&
          date.date <= this.dateTo &&
          date.status &&
          date.status !== SchoolStatus.CLOSED
        );
      })
      .sort(byKeys('date'));
  }

  @computed('days.[]')
  get weeks() {
    return this.days.reduce((acc, date) => {
      acc[date.week] = acc[date.week] ? acc[date.week] + 1 : 1;
      return acc;
    }, {});
  }

  @computed('days.[]')
  get firstDayOfWeek() {
    let week = -1;
    let idx = 0;

    return this.days.reduce((acc, day) => {
      if (week < day.week) {
        acc.push(idx);
        week = day.week;
      }

      idx++;

      return acc;
    }, []);
  }

  @computed('days.[]')
  get periodsByDay() {
    return this.days.reduce((acc, day) => {
      acc[day.date] = this.dates.dayForDate(day.date).periodIds.length;
      return acc;
    }, {});
  }

  @computed
  get codeMap() {
    return this.attendanceCodes.allCodes.reduce((map, code) => {
      map[code.code] = code.halfDay;
      return map;
    }, {});
  }

  @computed('model.{people.[],nzMoeDays.[]}')
  get peopleNzMoeDays() {
    return this.model.nzMoeDays.reduce((acc, record) => {
      let personId = get(record, 'person.id');

      if (!acc.hasOwnProperty(personId)) {
        acc[personId] = [];
      }

      acc[personId].push(record);

      return acc;
    }, {});
  }

  @computed('peopleNzMoeDays', 'term')
  get people() {
    return compact(
      this.model.people.map((person) => {
        let nzMoeDays = this.peopleNzMoeDays[person.id];

        nzMoeDays = nzMoeDays ? nzMoeDays : [];

        let status = person.get('learner.enrolmentStub.status') ?? 0;
        let isCurrent =
          status === EnrolmentStatus.CURRENT ||
          status === EnrolmentStatus.PENDING;

        if (isCurrent || nzMoeDays.length > 0) {
          let dailyAttendance = this.days.map((day) =>
            this.computeDailyAttendance(day.date, nzMoeDays),
          );

          return {
            nsn: person.get('fields.nzMOE:nsn.value'),
            lastName: person.lastName,
            firstName: person.firstName,
            homeroom: person.homeroom ? person.homeroom.name : '',
            homeroomId: person.homeroom ? person.homeroom.id : '',
            year: person.currentYearLevel,
            fundingYearLevel: person.fundingYearLevel,
            ethnicity: person.displayEthnicity,
            dailyAttendance,
          };
        }
      }),
    );
  }

  @computed('people')
  get homerooms() {
    return uniqBy(
      this.people
        .map((person) => {
          return {
            id: person.homeroomId,
            name: person.homeroom,
          };
        })
        .filter((obj) => !isEmpty(obj.id)),
      'id',
    ).sort(byKeys('name'));
  }

  @sort('homerooms', naturalSort('name'))
  sortedHomeRooms;

  @computed('people', 'sortDir', 'sortProp')
  get sortedPeople() {
    let people = sortBy(this.people, this.sortProp);

    return this.sortDir === 'asc' ? people : people.reverse();
  }

  computeDailyAttendance(date, nzMoeDays) {
    let nzMoeDay = nzMoeDays.filter((item) => item.date === date);
    if (nzMoeDay.length) {
      // There is only a single record recorded per day for half days.
      switch (nzMoeDay[0].present) {
        case 0:
          return '0';
        case 1:
          return '½';
        case 2:
          return '1';
      }
    }

    return '-';
  }

  parseCell(cell) {
    // Deep clone the cell, so we don't change the actual cell.
    let clone = cell.cloneNode(true);

    // Replace all title-bearing elements with their titles.
    clone.querySelectorAll('[title]').forEach((el) => {
      let textNode = document.createTextNode(el.title);
      el.parentNode.replaceChild(textNode, el);
    });

    // Remove sort direction icons
    clone
      .querySelectorAll('.md-sort-icon')
      .forEach((el) => el.parentNode.removeChild(el));

    // Return the element's innerText, with all excess whitespace removed.
    return clone.innerText.replace(/\s+/g, ' ').trim();
  }

  // excelFileName builds a date/term sensitive file name.
  @computed('termId', 'dateTo', 'dateFrom', 'term')
  get excelFileName() {
    let info = `${this.dateFrom} - ${this.dateTo}`;
    if (!isEmpty(this.termId)) {
      info = `Term ${this.term.term} ${this.term.academicYear}`;
    }
    return `${this.title} for ${info}`;
  }

  // _datesChanged updates the "new" date properties to keep them in line with the query params.
  @observes('dateFrom', 'dateTo')
  _datesChanged() {
    this.newDateFrom = this.dateFrom;
    this.newDateTo = this.dateTo;
  }

  // updateDates should be run debounced in order to prevent the route's model reloading
  // as soon as a "valid" date is entered, as the first digit of the year will make it "valid".
  updateDates() {
    let props = {
      termId: '',
    };
    let hasNewValue = false;

    if (!isEmpty(this.newDateFrom)) {
      props.dateFrom = this.newDateFrom;
      props.newDateFrom = null;
      hasNewValue = true;
    }

    if (!isEmpty(this.newDateTo)) {
      props.dateTo = this.newDateTo;
      props.newDateTo = null;
      hasNewValue = true;
    }

    if (hasNewValue) {
      this.setProperties(props);
    }
  }

  /**
   * onChangeDateFrom changes the newDateFrom field starts the debounce timer
   * to push this change to dateFrom and clear the currently set term (if any).
   *
   * @param {string} newDate - The new value of the date input field.
   */
  @action
  onChangeDateFrom(newDate) {
    this.newDateFrom = newDate;
    debounce(this, 'updateDates', 500);
  }

  /**
   * onChangeDateTo changes the newDateTo field starts the debounce timer
   * to push this change to dateTo and clear the currently set term (if any).
   *
   * @param {string} newDate - The new value of the date input field.
   */
  @action
  onChangeDateTo(newDate) {
    this.newDateTo = newDate;
    debounce(this, 'updateDates', 500);
  }

  @action
  onChangeSort({ sortProp, sortDir }) {
    this.sortProp = sortProp;
    this.sortDir = sortDir;
  }

  @action
  setSelectedTermId(term) {
    this.setProperties({
      termId: term.id,
      dateFrom: term.start,
      dateTo: term.end,
    });
  }

  @action
  findRecord(date, periodId, record) {
    return record.get('date') === date && record.get('period.id') === periodId;
  }

  @action
  dailyAttendance(date, records, periods) {
    records = records.filter((item) => item.date === date);

    if (records.length) {
      let present = records.filter(
        (record) => this.codeMap[record.code] === 'P',
      ).length;
      return `${present}/${periods.length}`;
    }

    return '-';
  }

  @action
  downloadFullXlsx() {
    let sheets = [];

    this.sortedHomeRooms.forEach((homeroom) => {
      let exportData = getTableExportData(
        `homeroom-${homeroom.id}`,
        'xlsx',
        this.parseCell,
      );

      sheets.push({
        name: cleanExcelSheetName(homeroom.name),
        data: exportData.rows,
        merges: exportData.merges,
      });
    });

    let params = {
      multiSheet: true,
      fileName: cleanFileName(`${this.excelFileName}.xlsx`),
    };
    return this.excel.export(sheets, params);
  }

  @action
  print() {
    let printer = window.open(
      'about:blank',
      '_blank',
      'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=700,width=1024',
    );
    let classLists = document.querySelectorAll('.class-list');

    printer.document.body.innerHTML = getTablePrintCss(true);

    printer.document.body.innerHTML += `<style>.table-tools button { display: none; }</style>`;

    classLists.forEach((classList, index) => {
      if (index !== 0) {
        printer.document.body.innerHTML += `<div style="page-break-before: always"></div>`;
      }

      printer.document.body.innerHTML += classList.outerHTML;
    });

    later(
      this,
      (printer) => {
        printer.print();
        printer.close();
      },
      printer,
      500,
    );
  }
}
